import { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/molecules/Card';
import { Badge, type BadgeVariant } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Spinner } from '@/components/atoms/Spinner';
import {
  invitationsAPI,
  type Invitation,
  type InvitationStatus,
} from '@/services/api/invitations.api';
import styles from './PendingInvitations.module.scss';

export interface PendingInvitationsProps {
  /** Solo backoffice: empresa cuyas invitaciones se listan. */
  companyId?: string;
  /** Cambiar este valor fuerza recargar la lista (p. ej. tras invitar). */
  refreshKey?: number;
}

const STATUS: Record<InvitationStatus, { label: string; variant: BadgeVariant }> = {
  pending: { label: 'Pending', variant: 'warning' },
  accepted: { label: 'Accepted', variant: 'success' },
  expired: { label: 'Expired', variant: 'error' },
  revoked: { label: 'Revoked', variant: 'secondary' },
};

function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString();
}

export function PendingInvitations({ companyId, refreshKey = 0 }: PendingInvitationsProps) {
  const [items, setItems] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(false);
    try {
      setItems(await invitationsAPI.list(companyId));
    } catch {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const handleResend = async (id: string) => {
    setBusyId(id);
    try {
      await invitationsAPI.resend(id);
      await load();
    } catch {
      /* noop: se mantiene el estado actual */
    } finally {
      setBusyId(null);
    }
  };

  const handleRevoke = async (id: string) => {
    setBusyId(id);
    try {
      await invitationsAPI.revoke(id);
      await load();
    } catch {
      /* noop */
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card className={styles.pendingInvitations}>
      <div className={styles.pendingInvitations__header}>
        <h2 className={styles.pendingInvitations__title}>Invitations</h2>
        <Button variant="ghost" size="sm" onClick={() => void load()}>
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className={styles.pendingInvitations__center}>
          <Spinner aria-label="Loading invitations" />
        </div>
      ) : error ? (
        <p className={styles.pendingInvitations__empty}>Couldn&apos;t load invitations.</p>
      ) : items.length === 0 ? (
        <p className={styles.pendingInvitations__empty}>No invitations yet.</p>
      ) : (
        <div className={styles.pendingInvitations__tableWrap}>
          <table className={styles.pendingInvitations__table}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Status</th>
                <th>Sent</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((inv) => {
                const s = STATUS[inv.status];
                const canManage = inv.status === 'pending' || inv.status === 'expired';
                return (
                  <tr key={inv.id}>
                    <td>{inv.email}</td>
                    <td>
                      <Badge variant={s.variant} size="sm">
                        {s.label}
                      </Badge>
                    </td>
                    <td>{formatDate(inv.createdAt)}</td>
                    <td className={styles.pendingInvitations__actions}>
                      {canManage && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => void handleResend(inv.id)}
                            disabled={busyId === inv.id}
                          >
                            Resend
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => void handleRevoke(inv.id)}
                            disabled={busyId === inv.id}
                          >
                            Revoke
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default PendingInvitations;
